import { useMemo, useState } from 'react'
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Message,
  Select,
  Switch,
} from '@arco-design/web-react'
import { IconPlus, IconRefresh, IconEdit } from '@arco-design/web-react/icon'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listAdminSubscriptionPlansApi,
  createAdminSubscriptionPlanApi,
  updateAdminSubscriptionPlanApi,
  patchSubscriptionPlanEnabledApi,
  type SubscriptionPlan,
  type SubscriptionPlanRow,
} from '@/api/subscription'
import { renderTimestamp } from '@/utils/render'
import { displayAmountToQuota, quotaToDisplayAmount, renderQuota } from '@/utils/quota'
import PageHeader from '@/components/Layout/PageHeader'
import { getFormValidateErrorMessage } from '@/utils/helpers'

const DURATION_UNITS: { value: string; label: string }[] = [
  { value: 'year', label: '年' },
  { value: 'month', label: '月' },
  { value: 'day', label: '日' },
  { value: 'hour', label: '小时' },
  { value: 'custom', label: '自定义(秒)' },
]

const RESET_PERIODS: { value: string; label: string }[] = [
  { value: 'never', label: '不重置' },
  { value: 'daily', label: '每天' },
  { value: 'weekly', label: '每周' },
  { value: 'monthly', label: '每月' },
  { value: 'custom', label: '自定义(秒)' },
]

function formatDuration(p: SubscriptionPlan): string {
  if (p.duration_unit === 'custom') {
    return `${p.custom_seconds} 秒`
  }
  const u = DURATION_UNITS.find((x) => x.value === p.duration_unit)?.label || p.duration_unit
  return `${p.duration_value} ${u}`
}

type FormValues = {
  title: string
  subtitle?: string
  price_amount: number
  duration_unit: string
  duration_value: number
  custom_seconds: number
  total_amount_display: number
  max_purchase_per_user: number
  upgrade_group: string
  quota_reset_period: string
  quota_reset_custom_seconds: number
  sort_order: number
  stripe_price_id: string
  creem_product_id: string
  enabled: boolean
}

function planToForm(p: SubscriptionPlan): FormValues {
  return {
    title: p.title,
    subtitle: p.subtitle || '',
    price_amount: p.price_amount,
    duration_unit: p.duration_unit || 'month',
    duration_value: p.duration_value || 1,
    custom_seconds: Number(p.custom_seconds) || 0,
    total_amount_display: quotaToDisplayAmount(p.total_amount),
    max_purchase_per_user: p.max_purchase_per_user ?? 0,
    upgrade_group: p.upgrade_group || '',
    quota_reset_period: p.quota_reset_period || 'never',
    quota_reset_custom_seconds: Number(p.quota_reset_custom_seconds) || 0,
    sort_order: p.sort_order ?? 0,
    stripe_price_id: p.stripe_price_id || '',
    creem_product_id: p.creem_product_id || '',
    enabled: p.enabled !== false,
  }
}

function defaultForm(): FormValues {
  return {
    title: '',
    subtitle: '',
    price_amount: 0,
    duration_unit: 'month',
    duration_value: 1,
    custom_seconds: 0,
    total_amount_display: 0,
    max_purchase_per_user: 0,
    upgrade_group: '',
    quota_reset_period: 'never',
    quota_reset_custom_seconds: 0,
    sort_order: 0,
    stripe_price_id: '',
    creem_product_id: '',
    enabled: true,
  }
}

function formToPlanPayload(v: FormValues): Partial<SubscriptionPlan> {
  return {
    title: v.title.trim(),
    subtitle: v.subtitle?.trim() || '',
    price_amount: Number(v.price_amount) || 0,
    currency: 'USD',
    duration_unit: v.duration_unit,
    duration_value: Math.max(1, Number(v.duration_value) || 1),
    custom_seconds: v.duration_unit === 'custom' ? Math.max(0, Number(v.custom_seconds) || 0) : 0,
    max_purchase_per_user: Math.max(0, Number(v.max_purchase_per_user) || 0),
    total_amount: displayAmountToQuota(v.total_amount_display),
    upgrade_group: v.upgrade_group?.trim() || '',
    quota_reset_period: v.quota_reset_period,
    quota_reset_custom_seconds:
      v.quota_reset_period === 'custom' ? Math.max(0, Number(v.quota_reset_custom_seconds) || 0) : 0,
    sort_order: Number(v.sort_order) || 0,
    stripe_price_id: v.stripe_price_id?.trim() || '',
    creem_product_id: v.creem_product_id?.trim() || '',
    enabled: !!v.enabled,
  }
}

export default function SubscriptionPage() {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<SubscriptionPlan | null>(null)
  const [form] = Form.useForm<FormValues>()
  const [durationUnit, setDurationUnit] = useState<string>('month')
  const [resetPeriod, setResetPeriod] = useState<string>('never')

  const qc = useQueryClient()
  const list = useQuery({
    queryKey: ['subscription-admin-plans'],
    queryFn: listAdminSubscriptionPlansApi,
  })

  const rows: SubscriptionPlanRow[] = list.data || []
  const total = rows.length
  const pageData = useMemo(() => {
    const start = (page - 1) * pageSize
    return rows.slice(start, start + pageSize)
  }, [rows, page, pageSize])

  const createMut = useMutation({
    mutationFn: (plan: Partial<SubscriptionPlan>) => createAdminSubscriptionPlanApi(plan),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['subscription-admin-plans'] })
    },
  })
  const updateMut = useMutation({
    mutationFn: ({ id, plan }: { id: number; plan: Partial<SubscriptionPlan> }) =>
      updateAdminSubscriptionPlanApi(id, plan),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['subscription-admin-plans'] })
    },
  })

  const openCreate = () => {
    setEditing(null)
    const init = defaultForm()
    form.setFieldsValue(init)
    setDurationUnit(init.duration_unit)
    setResetPeriod(init.quota_reset_period)
    setModalOpen(true)
  }

  const openEdit = (row: SubscriptionPlan) => {
    setEditing(row)
    const init = planToForm(row)
    form.setFieldsValue(init)
    setDurationUnit(init.duration_unit)
    setResetPeriod(init.quota_reset_period)
    setModalOpen(true)
  }

  const handleSubmit = async () => {
    try {
      const v = await form.validate() as FormValues
      const payload = formToPlanPayload(v)
      if (editing) {
        await updateMut.mutateAsync({ id: editing.id, plan: { ...editing, ...payload, id: editing.id } })
      } else {
        await createMut.mutateAsync(payload)
      }
      Message.success('已保存')
      setModalOpen(false)
    } catch (err: unknown) {
      Message.error(getFormValidateErrorMessage(err))
    }
  }

  const handleToggleEnabled = async (plan: SubscriptionPlan, enabled: boolean) => {
    try {
      await patchSubscriptionPlanEnabledApi(plan.id, enabled)
      Message.success(enabled ? '已启用' : '已禁用')
      await qc.invalidateQueries({ queryKey: ['subscription-admin-plans'] })
    } catch (err: unknown) {
      if (err instanceof Error) Message.error(err.message)
    }
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 72, render: (_: unknown, row: SubscriptionPlanRow) => row.plan.id },
    {
      title: '标题',
      width: 200,
      render: (_: unknown, row: SubscriptionPlanRow) => (
        <div>
          <div className="font-medium text-[#0F172A]">{row.plan.title}</div>
          {row.plan.subtitle ? <div className="text-xs text-[#6B7280] truncate max-w-[200px]">{row.plan.subtitle}</div> : null}
        </div>
      ),
    },
    {
      title: '价格 (USD)',
      width: 110,
      render: (_: unknown, row: SubscriptionPlanRow) => (
        <span className="tabular-nums">{row.plan.price_amount?.toFixed?.(2) ?? row.plan.price_amount}</span>
      ),
    },
    {
      title: '时长',
      width: 120,
      render: (_: unknown, row: SubscriptionPlanRow) => formatDuration(row.plan),
    },
    {
      title: '总额度',
      width: 120,
      render: (_: unknown, row: SubscriptionPlanRow) => renderQuota(row.plan.total_amount),
    },
    {
      title: '启用',
      width: 100,
      render: (_: unknown, row: SubscriptionPlanRow) => (
        <Switch
          size="small"
          checked={row.plan.enabled}
          onChange={(checked) => handleToggleEnabled(row.plan, checked)}
        />
      ),
    },
    { title: '排序', dataIndex: 'sort_order', width: 72, render: (_: unknown, row: SubscriptionPlanRow) => row.plan.sort_order },
    {
      title: '更新',
      width: 160,
      render: (_: unknown, row: SubscriptionPlanRow) =>
        row.plan.updated_at ? renderTimestamp(row.plan.updated_at) : '-',
    },
    {
      title: '操作',
      fixed: 'right' as const,
      width: 100,
      render: (_: unknown, row: SubscriptionPlanRow) => (
        <Button size="small" type="text" icon={<IconEdit />} onClick={() => openEdit(row.plan)}>
          编辑
        </Button>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title="订阅管理"
        subtitle="维护订阅套餐，字段与后端 /api/subscription/admin/plans 保持一致"
        actions={
          <Space>
            <Button icon={<IconRefresh />} onClick={() => list.refetch()}>
              刷新
            </Button>
            <Button type="primary" icon={<IconPlus />} onClick={openCreate}>
              新增套餐
            </Button>
          </Space>
        }
      />
      <Card className="!rounded-2xl" bordered={false}>
        <div className="mb-4 text-[13px] text-[#6B7280]">
          启用/禁用使用 PATCH 仅更新状态；编辑将提交完整套餐字段。后端不提供删除套餐接口。
        </div>
        <Table
          rowKey={(r) => String(r.plan.id)}
          loading={list.isLoading}
          columns={columns}
          data={pageData}
          scroll={{ x: 1100 }}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: (p, ps) => {
              setPage(p)
              setPageSize(ps)
            },
          }}
        />
      </Card>

      <Modal
        title={editing ? '编辑套餐' : '新增套餐'}
        style={{ width: 640 }}
        visible={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        confirmLoading={createMut.isPending || updateMut.isPending}
        unmountOnExit
      >
        <Form
          form={form}
          layout="vertical"
          onValuesChange={(_, all) => {
            if (all?.duration_unit !== undefined) setDurationUnit(String(all.duration_unit))
            if (all?.quota_reset_period !== undefined) setResetPeriod(String(all.quota_reset_period))
          }}
        >
          <Form.Item field="title" label="套餐标题" rules={[{ required: true, message: '必填' }]}>
            <Input placeholder="显示给用户的名称" />
          </Form.Item>
          <Form.Item field="subtitle" label="副标题">
            <Input />
          </Form.Item>
          <Form.Item field="price_amount" label="价格 (USD)">
            <InputNumber min={0} max={9999} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Space className="w-full" size={12}>
            <Form.Item className="flex-1" field="duration_unit" label="周期单位" style={{ width: 200 }}>
              <Select options={DURATION_UNITS} />
            </Form.Item>
            <Form.Item className="flex-1" field="duration_value" label="周期数值" style={{ width: 200 }}>
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          {durationUnit === 'custom' ? (
            <Form.Item field="custom_seconds" label="自定义时长（秒）" rules={[{ required: true, message: '必填' }]}>
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          ) : null}
          <Form.Item field="total_amount_display" label="套餐内总额度（按站点展示货币）">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <p className="text-xs text-[#9CA3AF] -mt-2 mb-2">
            0 表示不限制；保存时会按站点倍率换算为内部额度单位。
          </p>
          <Form.Item field="max_purchase_per_user" label="每用户可购买次数（0=不限制）">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item field="upgrade_group" label="购买后升级到的分组（空=不变）" extra="须为系统中已存在分组键">
            <Input placeholder="如 default" />
          </Form.Item>
          <Form.Item field="quota_reset_period" label="额度重置周期">
            <Select options={RESET_PERIODS} />
          </Form.Item>
          {resetPeriod === 'custom' ? (
            <Form.Item
              field="quota_reset_custom_seconds"
              label="自定义重置秒数"
              rules={[{ required: true, message: '必填' }]}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          ) : null}
          <Form.Item field="sort_order" label="排序（越大越靠前）">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item field="stripe_price_id" label="Stripe Price ID（可选）">
            <Input />
          </Form.Item>
          <Form.Item field="creem_product_id" label="Creem Product ID（可选）">
            <Input />
          </Form.Item>
          <Form.Item field="enabled" label="新建时启用" triggerPropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
